/** Stock lot optimization. No Angular, quotes, storage or mutations of the supplied facts. */
export interface PhaRoundingStock {
    id: number;
    symbol: string;
    shares: number;
    price: number;
    fixed: boolean;
    minimumShares: number;
}
export interface PhaRoundingInput {
    stocks: PhaRoundingStock[];
    deviationPercent: number;
    excluded: string[];
}
export interface PhaRoundingLine extends PhaRoundingStock {
    afterShares: number;
    beforeWeight: number;
    afterWeight: number;
    relativeDeviation: number;
    beforeCalls: number;
    afterCalls: number;
}
export interface PhaRoundingReport {
    lines: PhaRoundingLine[];
    excluded: string[];
    deviationPercent: number;
    beforeInvestment: number;
    afterInvestment: number;
    beforeCoveredValue: number;
    afterCoveredValue: number;
    beforeCalls: number;
    afterCalls: number;
    buys: number;
    sales: number;
    cashReleased: number;
}
export interface PhaRoundingResult {
    status: 'optimal' | 'no-improvement' | 'invalid' | 'limit' | 'cancelled';
    reason?: string;
    report?: PhaRoundingReport;
}
interface Range { lo: number[]; hi: number[]; }
const EPS = 1e-10;
const MONEY_SCALE = 10000;
const now = () => typeof performance !== 'undefined' ? performance.now() : Date.now();
const dollars = (v: number) => Math.round(v / MONEY_SCALE * 100) / 100;

/** Bounds are conservative; leaves use actual, unrounded post-run stock weights. */
export class PhaRoundingSearch {
    private readonly stocks: PhaRoundingStock[];
    private readonly prices: number[];
    private readonly original: number[];
    private readonly budget: number;
    private readonly deviation: number;
    private readonly deviationPercent: number;
    private readonly excluded: string[];
    private readonly start = now();
    private readonly stack: Range[] = [];
    private readonly seededLots = new Set<string>();
    private best: number[];
    private bestCovered: number;
    private readonly originalCovered: number;
    private bestDrift = 0;
    private bestTurnover = 0;
    private nodes = 0;
    private outcome: PhaRoundingResult | null = null;

    constructor(input: PhaRoundingInput, private readonly nodeLimit = 1000000,
                private readonly timeLimitMs = 5000) {
        this.stocks = input.stocks.map(s => ({...s})).sort((a, b) => a.symbol.localeCompare(b.symbol) || a.id - b.id);
        this.prices = this.stocks.map(s => Math.round(s.price * MONEY_SCALE));
        this.original = this.stocks.map(s => s.shares);
        this.budget = this.cost(this.original);
        this.deviation = input.deviationPercent / 100;
        this.deviationPercent = input.deviationPercent;
        this.excluded = input.excluded.slice();
        this.best = this.original.slice();
        this.originalCovered = this.bestCovered = this.covered(this.original);
        if (!this.stocks.length || !Number.isFinite(this.deviation) || this.deviation < 0 || this.deviation > 1
            || !Number.isSafeInteger(this.budget) || !(this.budget > 0)
            || this.stocks.some((s, i) => !Number.isSafeInteger(s.shares) || s.shares <= 0
                || !Number.isFinite(s.price) || !(s.price > 0) || this.prices[i] <= 0
                || !Number.isSafeInteger(s.minimumShares) || s.minimumShares < 0 || s.minimumShares > s.shares
                || !Number.isSafeInteger(s.shares * this.prices[i]))) {
            this.outcome = {status: 'invalid', reason: 'Stocks need positive prices, whole quantities and sufficient existing backing.'};
            return;
        }
        this.stack.push({lo: this.stocks.map(s => s.fixed ? s.shares : s.minimumShares),
            hi: this.stocks.map(s => s.fixed ? s.shares : Math.floor(s.shares * (1 + this.deviation) + EPS))});
    }

    get result(): PhaRoundingResult | null { return this.outcome; }
    private cost(q: number[]): number { return q.reduce((v, n, i) => v + n * this.prices[i], 0); }
    private covered(q: number[]): number { return q.reduce((v, n, i) => v + Math.floor(n / 100) * 100 * this.prices[i], 0); }
    private drift(q: number[], total = this.cost(q)): number {
        return q.reduce((v, n, i) => v + Math.pow(n * this.budget / (this.original[i] * total) - 1, 2), 0);
    }
    private accept(q: number[]): void {
        const total = this.cost(q), covered = this.covered(q);
        if (total > this.budget || !total || covered <= this.originalCovered || covered < this.bestCovered
            || q.some((n, i) => Math.abs(n * this.budget / (this.original[i] * total) - 1) > this.deviation + EPS)) { return; }
        const drift = this.drift(q, total);
        const traded = q.reduce((v, n, i) => v + Math.abs(n - this.original[i]) * this.prices[i], 0);
        const lexical = q.findIndex((n, i) => n !== this.best[i]);
        if (covered > this.bestCovered || drift < this.bestDrift - EPS
            || Math.abs(drift - this.bestDrift) <= EPS && (traded < this.bestTurnover
                || traded === this.bestTurnover && lexical >= 0 && q[lexical] < this.best[lexical])) {
            this.best = q.slice(); this.bestCovered = covered; this.bestDrift = drift; this.bestTurnover = traded;
        }
    }

    /** Cooperative slice; the caller yields to the browser between slices. */
    step(sliceMs = 8): PhaRoundingResult | null {
        if (this.outcome) { return this.outcome; }
        const until = now() + sliceMs;
        while (this.stack.length) {
            if (this.nodes >= this.nodeLimit || now() - this.start >= this.timeLimitMs) {
                return this.outcome = {status: 'limit', reason: 'Calculation limit reached. Try a smaller deviation or fewer unlocked stocks.'};
            }
            if (now() >= until) { return null; }
            this.nodes++;
            const range = this.stack.pop();
            if (!this.tighten(range)) { continue; }
            const {lo, hi} = range;
            const upper = Math.min(this.budget, this.covered(hi));
            if (upper <= this.originalCovered || upper < this.bestCovered) { continue; }
            const totalLo = Math.max(this.cost(lo), this.bestCovered), totalHi = Math.min(this.cost(hi), this.budget);
            const lowerDrift = lo.reduce((sum, n, i) => {
                const a = n * this.budget / (this.original[i] * totalHi) - 1;
                const b = hi[i] * this.budget / (this.original[i] * totalLo) - 1;
                return sum + (a > 0 ? a * a : b < 0 ? b * b : 0);
            }, 0);
            if (upper === this.bestCovered && lowerDrift > this.bestDrift + EPS) { continue; }
            if (lo.every((n, i) => n === hi[i])) { this.accept(lo); continue; }
            const fixedLots = lo.every((n, i) => Math.floor(n / 100) === Math.floor(hi[i] / 100));
            if (fixedLots) { this.seed(range, until); }
            let split = -1, largest = -1;
            hi.forEach((n, i) => {
                if (n === lo[i]) { return; }
                const lots = Math.floor(n / 100) - Math.floor(lo[i] / 100);
                const size = lots ? 1000 + lots : (n - lo[i]) / this.original[i];
                if (size > largest) { largest = size; split = i; }
            });
            const midpoint = Math.floor((lo[split] + hi[split]) / 2);
            const boundary = Math.ceil((midpoint + 1) / 100) * 100 - 1;
            const cut = !fixedLots && boundary >= lo[split] && boundary < hi[split] ? boundary : midpoint;
            const left = {lo: lo.slice(), hi: hi.slice()}, right = {lo: lo.slice(), hi: hi.slice()};
            left.hi[split] = cut; right.lo[split] = cut + 1;
            // Search high-lot children first; within a lot prefer the original quantity.
            const preferRight = !fixedLots || this.original[split] > cut;
            this.stack.push(preferRight ? left : right, preferRight ? right : left);
        }
        return this.outcome = this.bestCovered > this.originalCovered
            ? {status: 'optimal', report: this.report()}
            : {status: 'no-improvement', reason: 'No improvement within these limits.'};
    }

    private tighten(range: Range): boolean {
        const {lo, hi} = range;
        for (let pass = 0; pass < 8; pass++) {
            const minimumCost = this.cost(lo);
            const totalLo = Math.max(minimumCost, this.bestCovered), totalHi = Math.min(this.cost(hi), this.budget);
            if (totalLo > totalHi) { return false; }
            let changed = false;
            for (let i = 0; i < lo.length; i++) {
                const low = Math.max(lo[i], Math.ceil((1 - this.deviation) * this.original[i] * totalLo / this.budget - EPS));
                const high = Math.min(hi[i], Math.floor((1 + this.deviation) * this.original[i] * totalHi / this.budget + EPS),
                    Math.floor((this.budget - minimumCost + lo[i] * this.prices[i]) / this.prices[i] + EPS));
                if (low > high) { return false; }
                changed = changed || low !== lo[i] || high !== hi[i]; lo[i] = low; hi[i] = high;
            }
            if (!changed) { break; }
        }
        return true;
    }

    /** Feasible incumbent only; the exhaustive bounded search still proves every objective. */
    private seed(range: Range, until: number): void {
        const key = range.lo.map(n => Math.floor(n / 100)).join(',');
        if (this.seededLots.has(key)) { return; } this.seededLots.add(key);
        const q = range.lo.slice();
        let total = this.cost(q);
        for (let step = 0; step < 1000; step++) {
            if (now() >= until) { break; }
            let chosen = -1, gain = -Infinity;
            const current = this.drift(q, total);
            for (let i = 0; i < q.length; i++) {
                if (q[i] >= Math.min(range.hi[i], this.original[i]) || total + this.prices[i] > this.budget) { continue; }
                q[i]++;
                const improvement = (current - this.drift(q, total + this.prices[i])) / this.prices[i];
                q[i]--;
                if (improvement > gain) { gain = improvement; chosen = i; }
            }
            if (chosen < 0 || gain <= 0) { break; }
            q[chosen]++; total += this.prices[chosen];
        }
        this.accept(q);
    }

    private report(): PhaRoundingReport {
        const total = this.cost(this.best);
        const buys = this.best.reduce((v, n, i) => v + Math.max(n - this.original[i], 0) * this.prices[i], 0);
        const sales = this.best.reduce((v, n, i) => v + Math.max(this.original[i] - n, 0) * this.prices[i], 0);
        return {lines: this.stocks.map((s, i) => ({...s, afterShares: this.best[i],
            beforeWeight: s.shares * this.prices[i] / this.budget * 100,
            afterWeight: this.best[i] * this.prices[i] / total * 100,
            relativeDeviation: (this.best[i] * this.budget / (s.shares * total) - 1) * 100,
            beforeCalls: Math.floor(s.shares / 100), afterCalls: Math.floor(this.best[i] / 100)})),
            excluded: this.excluded.slice(), deviationPercent: this.deviationPercent,
            beforeInvestment: dollars(this.budget), afterInvestment: dollars(total),
            beforeCoveredValue: dollars(this.originalCovered), afterCoveredValue: dollars(this.bestCovered),
            beforeCalls: this.original.reduce((v, n) => v + Math.floor(n / 100), 0),
            afterCalls: this.best.reduce((v, n) => v + Math.floor(n / 100), 0),
            buys: dollars(buys), sales: dollars(sales), cashReleased: dollars(this.budget - total)};
    }
}

export async function calculateRounding(input: PhaRoundingInput, cancelled: () => boolean,
                                        yieldToUi: () => Promise<void> = () => new Promise(r => setTimeout(r, 0))): Promise<PhaRoundingResult> {
    const search = new PhaRoundingSearch(input);
    while (!cancelled()) {
        const result = search.step();
        if (result) { return result; }
        await yieldToUi();
    }
    return {status: 'cancelled', reason: 'Calculation cancelled.'};
}
